// Whether a listed price needs a "plus VAT" qualifier next to it, and the exact
// wording of that qualifier.
//
// WHY THIS EXISTS. supa.place is Merchant of Record for consumption tax and adds
// VAT on top of the seller's price at the payment step. That is fine for the
// CHECKOUT, where the Consumer Rights Directive (2011/83/EU, Art. 6(1)(e)) only
// requires the tax-inclusive total before the consumer is bound. It is not fine
// for the marketplace and product pages: the Price Indication Directive
// (98/6/EC, Art. 2(a)) defines the "selling price" shown to a consumer as the
// final price INCLUDING VAT, and the Unfair Commercial Practices Directive
// (2005/29/EC, Art. 7(4)(c)) treats omitting it as a misleading omission. A bare
// seller price shown to an EU consumer who will be charged more at checkout is
// exactly the omission those instruments describe.
//
// The qualifier is the minimum honest fix: the price stays the seller's listed
// figure (which is what the seller set, what every other jurisdiction pays, and
// what the whole pricing UI is built around), and the buyer is told before they
// click that tax is added. It does not make the page fully Art. 2(a) compliant
// on its own -- only rendering an inclusive figure does that -- but it removes
// the misleading part, which is the buyer discovering the addition at checkout.
//
// THE SAME PROBLEM EXISTS IN THE UK, under different instruments. The Price
// Marking Order 2004 requires the selling price shown to a consumer to include
// VAT, and the Consumer Protection from Unfair Trading Regulations 2008 treat
// omitting an unavoidable added charge as a misleading omission, so a bare price
// shown to a UK consumer who is then charged 20% at checkout is the same
// omission the EU instruments describe. The qualifier is the same minimum fix.
//
// SCOPE IS EXACTLY WHAT WE CHARGE, AND NOTHING MORE. This must track what is
// actually COLLECTED, not what could be. Showing "plus VAT" to a buyer we then
// charge no VAT to is its own misrepresentation, and a worse one, because it
// overstates the price. Live collection is TAX_EU_ENABLED=1 / TAX_UK_ENABLED=1,
// so:
//
//   - GB is present, under supa.place's own UK VAT registration (GB525770187,
//     effective 2026-08-01) rather than the OSS scheme, which does not cover the
//     UK. It went in in the same change that set TAX_UK_ENABLED=1. THE COUPLING
//     RUNS BOTH WAYS: if UK collection is ever switched back off, GB has to come
//     out of here in that same change, or every UK price starts claiming a tax
//     we no longer add.
//   - The EEA three (IS/NO/LI) remain absent. We hold no registration in any of
//     them and charge their buyers nothing. They appear in
//     WITHDRAWAL_RIGHT_COUNTRIES because the withdrawal right applies there
//     regardless of tax, which is precisely why that list and this one are
//     separate and must not be merged.
//
// Mirrors the charged rows of RATE_TABLE in functions/src/lib/vat-rates.ts (two
// runtimes, two builds) and is locked to them by src/lib/vat-notice.test.ts, so
// a jurisdiction added or removed on one side fails the suite rather than
// silently showing the wrong qualifier.

/**
 * Member states where supa.place collects VAT under the EU OSS scheme.
 *
 * Kept separate from the UK below rather than merged into one literal, because
 * the two are locked to different halves of the server rate table: these are its
 * EU_OSS rows, GB is its UK_VAT row. One flat set would let a member state and
 * the UK swap places without the lock test noticing.
 */
export const EU_OSS_NOTICE_COUNTRIES: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

/**
 * Where supa.place collects VAT under its own UK registration, which is a
 * separate registration filed on a separate return and is why this is not simply
 * another entry in the OSS list.
 */
export const UK_NOTICE_COUNTRIES: ReadonlySet<string> = new Set(['GB']);

/**
 * Every jurisdiction where a listed price is exclusive of a tax the buyer will
 * actually be charged, and so needs the qualifier.
 */
export const VAT_NOTICE_COUNTRIES: ReadonlySet<string> = new Set([
  ...EU_OSS_NOTICE_COUNTRIES, ...UK_NOTICE_COUNTRIES,
]);

/**
 * The qualifier itself. Kept here rather than inline so the marketplace card and
 * the product page cannot drift apart, and so the wording is greppable when the
 * legal copy is next reviewed.
 *
 * Deliberately short. It sits beside a price in small grey type on a card that
 * may be one of forty on screen, and anything longer wraps and stops being read.
 * The full explanation (rate depends on your country, shown before you pay,
 * business buyers can reverse-charge) belongs on /terms, which this points at in
 * substance.
 */
export const VAT_NOTICE_LABEL = 'plus VAT';

/**
 * Whether to show the qualifier to a visitor resolved to this country.
 *
 * An unknown or empty country returns false. That is the deliberate direction:
 * the country comes from a GeoIP lookup that legitimately fails (VPNs, IPv6
 * ranges the DB does not carry, a missing .mmdb), and the cost of a false
 * negative is an EU buyer seeing the same bare price they see today, whereas a
 * false positive tells a US buyer they owe a tax they do not.
 *
 * @param country ISO 3166-1 alpha-2, any case. '' / null / undefined all mean
 *   "could not resolve".
 * @returns True when the price shown to this visitor excludes VAT they will be
 *   charged.
 */
export function showsVatNotice(country: string | null | undefined): boolean {
  if (!country) return false;
  const code = country.trim().toUpperCase();
  return VAT_NOTICE_COUNTRIES.has(VAT_TERRITORY_ALIASES[code] ?? code);
}

/**
 * Territories with their own ISO code that sit inside another country's VAT
 * area: Monaco is France for VAT, the Isle of Man is in the UK VAT area. GeoIP
 * returns MC / IM, neither is a row of the rate table, and the server now taxes
 * both through the country they belong to, so the qualifier has to show for
 * them too or their prices would rise at checkout unannounced.
 *
 * MIRROR of VAT_TERRITORY_ALIASES in functions/src/lib/tax-gating.ts, held equal
 * by src/lib/vat-notice.test.ts. Kept out of the two NOTICE_COUNTRIES sets on
 * purpose: those are locked to the rate table's rows, which these are not.
 */
export const VAT_TERRITORY_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  MC: 'FR',
  IM: 'GB',
});

// ── THE DIAMOND MEMBERSHIP PRICE, AND WHY THE VIEWER'S IP IS A SAFE GATE ─────
//
// <VatNotice> asks showsVatNotice(useGeoCountry()), the country the VIEWER'S IP
// resolves to. That is exactly the right question for a product price, where the
// viewer is the buyer. It is NOT obviously the right question for the Diamond
// membership price on /sell, /fees and /dashboard/progression, and the mismatch
// is worth stating because the answer is not "they are the same thing".
//
// The membership is supplied to the SELLER, so createDiamondSubscriptionIntent
// takes its place of supply from
//
//     resolveSupplyCountry(sellerCountry, ipCountry)
//
// where sellerCountry is their registered country (sellerProfiles/{uid}) and the
// rule, in functions/src/lib/tax-gating.ts, is one-directional: the declaration
// wins EXCEPT where honouring it would lower the tax, in which case the IP wins.
//
// THE PROPERTY THAT MAKES THE VIEWER'S IP SUFFICIENT is a consequence of that
// one-directionality: if the viewer's IP resolves to a region we charge, the
// membership is charged, whatever the registered country says. Enumerate it:
//   * declared out of scope (rate 0), ip in scope: the declaration would reduce
//     the tax, so the IP wins and the supply is in scope;
//   * declared in scope, ip in scope: whichever wins, both are in scope;
//   * declared absent, ip in scope: the IP is all there is.
// In every branch an in-scope IP yields an in-scope supply.
//
// So the gate can only ever fail in ONE direction, and it is the survivable one.
// It never shows the qualifier to a seller we do not charge, which vat-notice's
// header calls out as "its own misrepresentation, and a worse one", because it
// overstates the price. What it can do is stay silent for a seller registered in
// the EU or UK who is browsing from outside it (travelling, or on a VPN): they
// are charged on their registered country and see a bare figure here.
//
// That residual is deliberately left open rather than closed with a guess. The
// honest fix needs sellerCountry on the client, and no client code reads
// sellerProfiles/{uid} today; it is owner-read and mid-migration off
// users/{uid}. Adding a Firestore read and an auth-context field to move a grey
// span is not worth it while the binding disclosure is already made: the
// subscribe dialog itemises the real tax line before the seller is bound, which
// is what the Consumer Rights Directive Art. 6(1)(e) actually requires. What is
// missing without the qualifier is the ADVERTISED-price rule (the Price
// Indication Directive, the UK Price Marking Order), and that is what the
// qualifier restores for every seller browsing from where they are registered.
//
// The enumeration above is not left as prose: src/lib/vat-notice.test.ts scrapes
// the real resolveSupplyCountry out of tax-gating.ts and asserts the property
// across the country matrix, so a change to that rule fails the suite here
// rather than quietly turning this gate into an over-disclosure.
